// src/components/ToastProvider.jsx
import React, {
  createContext, useContext, useRef, useState, useEffect, useCallback
} from "react";
import styled, { css } from "styled-components";
import { createPortal } from "react-dom";
import { LuCircleCheck, LuX, LuInfo, LuTriangleAlert } from "react-icons/lu";

const ToastContext = createContext(null);

export function ToastProvider({ children, defaultDuration = 3000, hijackWindowAlert = true }) {
  const [toast, setToast] = useState(null); // { id, title, message, type, appearance, icon, duration }
  const [open, setOpen] = useState(false);

  const timerRef = useRef(null);
  const startRef = useRef(0);
  const remainingRef = useRef(0);

  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };

  const scheduleHide = useCallback((ms) => {
    clearTimer();
    startRef.current = Date.now();
    remainingRef.current = ms;
    timerRef.current = setTimeout(() => {
      setOpen(false); // anima saída; desmonta no onTransitionEnd
    }, ms);
  }, []);

  const hide = useCallback((immediate = false) => {
    clearTimer();
    if (immediate) { setOpen(false); setToast(null); return; }
    setOpen(false);
  }, []);

  const getIconFromKey = (key) => {
    if (React.isValidElement(key)) return key;
    switch (key) {
      case "alert": return <LuTriangleAlert size={22} />;
      case "info":  return <LuInfo size={22} />;
      case "check":
      default:      return <LuCircleCheck size={22} />;
    }
  };
  const defaultIconForType = (type) =>
    type === "error" ? "alert" : type === "info" ? "info" : "check";

  const show = useCallback((message, opts = {}) => {
    clearTimer();
    const type = opts.type ?? "success";
    const appearance = opts.appearance ?? type;
    const iconKey = opts.icon ?? defaultIconForType(type);

    const t = {
      id: Date.now(),
      message,
      title: opts.title,
      duration: opts.duration ?? defaultDuration,
      type,
      appearance,
      icon: iconKey
    };

    setToast(t);
    // garante um frame antes de abrir (transição suave)
    requestAnimationFrame(() => {
      setOpen(true);
      scheduleHide(t.duration);
    });
    return t.id;
  }, [defaultDuration, scheduleHide]);

  const pause = useCallback(() => {
    if (!timerRef.current) return;
    const elapsed = Date.now() - startRef.current;
    const left = Math.max(0, remainingRef.current - elapsed);
    remainingRef.current = left;
    clearTimer();
  }, []);

  const resume = useCallback(() => {
    if (!toast || !open) return; // não reagenda se já estiver fechando
    scheduleHide(Math.max(200, remainingRef.current || toast.duration || defaultDuration));
  }, [scheduleHide, toast, defaultDuration, open]);

  const api = React.useMemo(() => ({
    show,
    success: (m, o) => show(m, { ...o, type: "success" }),
    error:   (m, o) => show(m, { ...o, type: "error" }),
    info:    (m, o) => show(m, { ...o, type: "info" }),
    hide
  }), [show, hide]);

  useEffect(() => {
    if (!hijackWindowAlert) return;
    const prev = window.alert;
    window.alert = (msg) => show(String(msg), { type: "error" });
    return () => { window.alert = prev; };
  }, [hijackWindowAlert, show]);

  const handleCardTransitionEnd = useCallback((e) => {
    // desmonta só quando a OPACITY do CARD terminou
    if (e.target !== e.currentTarget) return;
    if (e.propertyName === "opacity" && !open && toast) setToast(null);
  }, [open, toast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        toast ? (
          <Viewport role="status" aria-live="polite">
            <Card
              key={toast.id}
              data-open={open}
              data-appearance={toast.appearance}
              onMouseEnter={pause}
              onMouseLeave={resume}
              onTransitionEnd={handleCardTransitionEnd}
            >
              <Glass aria-hidden />
              <IconWrap>{getIconFromKey(toast.icon)}</IconWrap>

              <Content>
                {toast.title && <Title>{toast.title}</Title>}
                <Message>{toast.message}</Message>
              </Content>

              <CloseButton onClick={() => hide()} aria-label="Fechar notificação">
                <LuX size={18} />
              </CloseButton>

              <Life
                style={{ transitionDuration: `${toast.duration ?? defaultDuration}ms` }}
                data-open={open}
              />
            </Card>
          </Viewport>
        ) : null,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider />");
  return ctx;
}

/* ====== styles ====== */
const Viewport = styled.div`
  position: fixed; inset: 0; pointer-events: none; z-index: 99999;
`;

const Card = styled.div`
  position: absolute;
  top: calc(14px + env(safe-area-inset-top, 0px));
  left: 50%;
  transform: translate3d(-50%, 0, 0) scale(1);
  pointer-events: auto;

  display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 12px;
  width: min(92vw, 560px);
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid rgba(255,255,255,.18);
  color: #fff;

  /* define cores como variáveis; o <Glass> lê daqui */
  --g1:#16a34a; --g2:#059669;
  &[data-appearance="error"]  { --g1:#ef4444; --g2:#dc2626; }
  &[data-appearance="info"]   { --g1:#3b82f6; --g2:#2563eb; }
  &[data-appearance="success"]{ --g1:#16a34a; --g2:#059669; }

  background: transparent;

  /* Transições estáveis (sem keyframes) */
  opacity: ${p => p["data-open"] ? 1 : 0};
  transform: ${p => p["data-open"]
    ? "translate3d(-50%, 0, 0) scale(1)"
    : "translate3d(-50%, -8px, 0) scale(.995)"};

  transition:
    opacity 200ms ease,
    transform 220ms cubic-bezier(.2,.8,.2,1);

  will-change: opacity, transform;
  backface-visibility: hidden;
  isolation: isolate;
  contain: paint;

  @media (prefers-reduced-motion: reduce) {
    transition: none; transform: translate3d(-50%, 0, 0); opacity: 1;
  }
`;

/* Camada de vidro/gradiente separada do card (evita flicker com backdrop-filter) */
const Glass = styled.span`
  position: absolute; inset: 0; border-radius: 16px; z-index: -1;
  background: linear-gradient(135deg, var(--g1) 0%, var(--g2) 100%);
  box-shadow: 0 14px 40px rgba(2, 6, 5, .28);
  border: 1px solid rgba(255,255,255,.18);
  backdrop-filter: blur(6px);
  pointer-events: none;
`;

const IconWrap = styled.div` display: grid; place-items: center; opacity: .98; `;
const Content  = styled.div` display: grid; gap: 2px; `;
const Title    = styled.div` font-weight: 800; line-height: 1.1; letter-spacing: .2px; `;
const Message  = styled.div` font-size: .96rem; opacity: .98; `;

const CloseButton = styled.button`
  all: unset; cursor: pointer; padding: 6px; border-radius: 12px; line-height: 0;
  background: rgba(255,255,255,.14);
  transition: transform .1s ease, background .15s ease, opacity .2s ease;
  &:hover { background: rgba(255,255,255,.20); }
  &:active { transform: translateY(1px) scale(.98); }
`;

const Life = styled.span`
  position: absolute; left: 12px; right: 12px; bottom: 8px; height: 3px;
  border-radius: 999px; background: rgba(255,255,255,.85);
  transform-origin: left;

  /* Transição por transform (evita reset de frame) */
  transform: ${p => p["data-open"] ? "scaleX(1)" : "scaleX(0)"};
  transition-property: transform, opacity;
  transition-timing-function: linear;

  /* some no close para não causar repaints extras */
  ${p => !p["data-open"] && css`opacity: 0;`}
`;
